import React, { useState, useEffect } from "react";
import { NavLink } from "react-router-dom";

const links = [
  { to: "/", label: "About" },
  { to: "/skills", label: "Skills" },
  { to: "/projects", label: "Projects" },
  { to: "/contact", label: "Contact" },
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close menu on route change / resize
  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth > 768) setMenuOpen(false);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800&family=DM+Sans:wght@400;500&display=swap');

        .nav-root {
          position: sticky;
          top: 0;
          z-index: 900;
          font-family: 'DM Sans', sans-serif;
          transition: background 0.35s ease, box-shadow 0.35s ease, border-color 0.35s ease;
          background: ${
            scrolled ? "rgba(10,10,15,0.85)" : "rgba(10,10,15,0.4)"
          };
          backdrop-filter: blur(14px);
          -webkit-backdrop-filter: blur(14px);
          border-bottom: 1px solid ${
            scrolled ? "rgba(255,255,255,0.07)" : "transparent"
          };
          box-shadow: ${scrolled ? "0 4px 32px rgba(0,0,0,0.4)" : "none"};
        }

        .nav-inner {
          max-width: 1100px;
          margin: 0 auto;
          padding: 0 24px;
          height: 64px;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        /* Logo */
        .nav-logo {
          text-decoration: none;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .nav-logo-text {
          font-family: 'Syne', sans-serif;
          font-size: 22px;
          font-weight: 800;
          letter-spacing: -0.03em;
          color: #f0f0f0;
          line-height: 1;
          transition: color 0.2s;
        }
        .nav-logo-text span {
          background: linear-gradient(135deg, #4ade80 0%, #22d3ee 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .nav-logo:hover .nav-logo-text { color: rgba(240,240,240,0.75); }

        /* Desktop links */
        .nav-links {
          display: flex;
          align-items: center;
          gap: 4px;
          list-style: none;
          margin: 0;
          padding: 0;
        }

        .nav-link-item {
          position: relative;
        }

        .nav-link-anchor {
          display: block;
          padding: 8px 16px;
          border-radius: 10px;
          font-size: 13.5px;
          font-weight: 500;
          letter-spacing: 0.03em;
          color: rgba(240,240,240,0.45);
          text-decoration: none;
          transition: color 0.2s, background 0.2s;
        }
        .nav-link-anchor:hover {
          color: rgba(240,240,240,0.85);
          background: rgba(255,255,255,0.05);
        }
        .nav-link-anchor.active {
          color: #4ade80;
          background: rgba(74,222,128,0.08);
        }

        /* Active dot */
        .nav-link-anchor.active::after {
          content: '';
          position: absolute;
          bottom: -2px;
          left: 50%;
          transform: translateX(-50%);
          width: 4px;
          height: 4px;
          border-radius: 50%;
          background: #4ade80;
        }

        /* CTA button */
        .nav-cta {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 8px 18px;
          border-radius: 10px;
          border: 1px solid rgba(74,222,128,0.35);
          background: rgba(74,222,128,0.08);
          color: #4ade80;
          font-family: 'DM Sans', sans-serif;
          font-size: 13px;
          font-weight: 500;
          letter-spacing: 0.04em;
          text-decoration: none;
          margin-left: 12px;
          transition: background 0.2s, border-color 0.2s, transform 0.2s, box-shadow 0.2s;
        }
        .nav-cta:hover {
          background: rgba(74,222,128,0.15);
          border-color: rgba(74,222,128,0.55);
          color: #4ade80;
          transform: translateY(-1px);
          box-shadow: 0 4px 16px rgba(74,222,128,0.15);
        }

        /* Hamburger */
        .nav-hamburger {
          display: none;
          flex-direction: column;
          justify-content: center;
          gap: 5px;
          width: 36px;
          height: 36px;
          padding: 6px;
          border-radius: 10px;
          border: 1px solid rgba(255,255,255,0.09);
          background: rgba(255,255,255,0.04);
          cursor: pointer;
          transition: background 0.2s, border-color 0.2s;
        }
        .nav-hamburger:hover {
          background: rgba(74,222,128,0.08);
          border-color: rgba(74,222,128,0.25);
        }

        .ham-bar {
          width: 100%;
          height: 1.5px;
          background: rgba(240,240,240,0.6);
          border-radius: 2px;
          transition: transform 0.3s ease, opacity 0.3s ease, background 0.2s;
          transform-origin: center;
        }
        .nav-hamburger.open .ham-bar:nth-child(1) { transform: translateY(6.5px) rotate(45deg); background: #4ade80; }
        .nav-hamburger.open .ham-bar:nth-child(2) { opacity: 0; transform: scaleX(0); }
        .nav-hamburger.open .ham-bar:nth-child(3) { transform: translateY(-6.5px) rotate(-45deg); background: #4ade80; }

        /* Mobile menu */
        .nav-mobile-menu {
          position: absolute;
          top: 64px;
          left: 0;
          right: 0;
          background: rgba(10,10,15,0.97);
          backdrop-filter: blur(20px);
          border-bottom: 1px solid rgba(255,255,255,0.07);
          overflow: hidden;
          max-height: 0;
          transition: max-height 0.4s cubic-bezier(0.4,0,0.2,1), opacity 0.3s ease;
          opacity: 0;
          z-index: 899;
        }
        .nav-mobile-menu.open {
          max-height: 320px;
          opacity: 1;
        }

        .nav-mobile-inner {
          padding: 16px 24px 24px;
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .nav-mobile-link {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 16px;
          border-radius: 12px;
          font-size: 14px;
          font-weight: 500;
          color: rgba(240,240,240,0.5);
          text-decoration: none;
          transition: color 0.2s, background 0.2s;
        }
        .nav-mobile-link:hover {
          color: rgba(240,240,240,0.9);
          background: rgba(255,255,255,0.05);
        }
        .nav-mobile-link.active {
          color: #4ade80;
          background: rgba(74,222,128,0.08);
        }
        .nav-mobile-link-num {
          font-size: 10px;
          font-weight: 600;
          letter-spacing: 0.1em;
          color: rgba(74,222,128,0.4);
          min-width: 20px;
        }
        .nav-mobile-link.active .nav-mobile-link-num { color: #4ade80; }

        .nav-mobile-divider {
          height: 1px;
          background: rgba(255,255,255,0.06);
          margin: 8px 0;
        }

        .nav-mobile-cta {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 12px;
          border-radius: 12px;
          border: 1px solid rgba(74,222,128,0.3);
          background: rgba(74,222,128,0.07);
          color: #4ade80;
          font-size: 13px;
          font-weight: 500;
          text-decoration: none;
          margin-top: 4px;
          transition: background 0.2s, border-color 0.2s;
        }
        .nav-mobile-cta:hover {
          background: rgba(74,222,128,0.13);
          border-color: rgba(74,222,128,0.5);
          color: #4ade80;
        }

        @media (max-width: 768px) {
          .nav-links { display: none; }
          .nav-cta { display: none; }
          .nav-hamburger { display: flex; }
          .nav-root { position: sticky; }
        }
      `}</style>

      <nav className="nav-root">
        <div className="nav-inner">
          {/* Logo */}
          <NavLink to="/" className="nav-logo">
            <span className="nav-logo-text">
              ade<span>lite</span>
            </span>
          </NavLink>

          {/* Desktop nav */}
          <ul className="nav-links">
            {links.map((l) => (
              <li key={l.to} className="nav-link-item">
                <NavLink
                  to={l.to}
                  end={l.to === "/"}
                  className={({ isActive }) =>
                    `nav-link-anchor ${isActive ? "active" : ""}`
                  }
                >
                  {l.label}
                </NavLink>
              </li>
            ))}
          </ul>

          {/* Hamburger */}
          <button
            className={`nav-hamburger ${menuOpen ? "open" : ""}`}
            onClick={() => setMenuOpen((o) => !o)}
            aria-label="Toggle menu"
            aria-expanded={menuOpen}
          >
            <span className="ham-bar" />
            <span className="ham-bar" />
            <span className="ham-bar" />
          </button>
        </div>

        {/* Mobile menu */}
        <div className={`nav-mobile-menu ${menuOpen ? "open" : ""}`}>
          <div className="nav-mobile-inner">
            {links.map((l, i) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                className={({ isActive }) =>
                  `nav-mobile-link ${isActive ? "active" : ""}`
                }
                onClick={() => setMenuOpen(false)}
              >
                <span className="nav-mobile-link-num">0{i + 1}</span>
                {l.label}
              </NavLink>
            ))}
          </div>
        </div>
      </nav>
    </>
  );
};

export default Navbar;
